import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { TutorService } from '../tutor.service';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ImageModule } from 'primeng/image';
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { Router } from '@angular/router';
import { CONSTANTS } from '../../../../core/constants';
import { GlobalFunctions } from '../../../../core/global-functions';
import { CommonService } from '../../../../core/common.service';
import { EducationFormComponent } from '../../../../shared/modals/tutor/education-form/education-form.component';

export const yearRangeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const startYear = control.get('start_year');
  const endYear = control.get('end_year');

  if (startYear && endYear && startYear.value && endYear.value && startYear.value > endYear.value) {
    return { yearRange: true };
  }

  return null;
};

@Component({
  selector: 'app-educations',
  standalone: true,
  imports: [CommonModule, ImageModule, ReactiveFormsModule, FormsModule, MultiSelectModule, DropdownModule, CheckboxModule, DialogModule, TooltipModule, ToastModule, EducationFormComponent],
  templateUrl: './educations.component.html',
  styleUrl: './educations.component.scss'
})
export class EducationsComponent implements OnInit {

  constants: any = CONSTANTS;

  educationForm: any = FormGroup;
  profileDetails: any;

  displayDialog: boolean = false;
  displaySkipDialog: boolean = false;
  editingIndex: number | null = null;
  isBtnLoading: boolean = false;
  yearList: any = [];

  constructor(
    private _formBuilder: FormBuilder,
    private _tutorServices: TutorService,
    public _globalFunctions: GlobalFunctions,
    private messageService: MessageService,
    private _router: Router,
    private _commonService: CommonService
  ) {
    const startYear = 1990;
    const currentYear = new Date().getFullYear();
    for (let year = startYear; year <= currentYear; year++) {
      this.yearList.push({ name: year.toString(), value: year });
    };
  }

  ngOnInit(): void {
    this.prepareEducationFormObj();
    this.getUserDetails();

    this.educationForm.get('do_you_have_higher_education_degree').valueChanges.subscribe((value: boolean) => {
      if (value) {
        this.education.setValidators([Validators.required, Validators.minLength(1)]);
        if (this.education.length === 0) {
          // No longer auto-adding to the array, just letting user click "+ Add Education" 
          // or we can leave it empty to show the "no records" state.
        }
      } else {
        this.education.clear();
        this.education.setValidators(null);
      }
      this.education.updateValueAndValidity();
    });
  }


  getUserDetails() {
    this._commonService.getProfile().subscribe({
      next: (result: any) => {
        if (result && result.success && result.data) {
          this.profileDetails = result.data;
          this._commonService.setProfileData(result.data); // Update local storage
          this.prepareEducationFormObj(result.data);
        }
      },
      error: (error: any) => {
        this._globalFunctions.errorHanding(error, this, true);
      }
    });
  }

  get education(): FormArray {
    return this.educationForm.get('education') as FormArray;
  }

  sortEducations() {
    this.education.controls.sort((a: any, b: any) => {
      const yearA = parseInt(a.get('start_year')?.value || 0);
      const yearB = parseInt(b.get('start_year')?.value || 0);
      return yearB - yearA;
    });
  }

  createEducationListForm(tempObj: any = {}) {
    return this._formBuilder.group({
      id: [tempObj?.id || ''],
      university_name: [tempObj?.university_name || '', [Validators.required]],
      university_address: [tempObj?.university_address || '', [Validators.required]],
      degree_type: [tempObj?.degree_type || '', [Validators.required]],
      specialization: [tempObj?.specialization || '', [Validators.required]],
      start_year: [tempObj?.start_year || '', [Validators.required]],
      end_year: [tempObj?.end_year || '', [Validators.required]],
      document: [tempObj?.document || ''],
    }, { validators: yearRangeValidator });
  }

  openDialog(index: number | null = null) {
    this.editingIndex = index;
    this.displayDialog = true;
  }

  onEducationSaved(savedItem: any) {
    if (this.editingIndex === null) {
      this.education.push(this.createEducationListForm(savedItem));
    } else {
      this.education.at(this.editingIndex).patchValue(savedItem);
    }
    this.sortEducations();
  }

  removeEducationItem(index: number) {
    const id = this.education.at(index).get('id')?.value;
    if (!id) {
      this.education.removeAt(index);
      return;
    }

    this._tutorServices.deleteEducation(id).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
          this.education.removeAt(index);
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: res.message });
        }
      },
      error: (err: any) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err.error?.message || 'Failed to delete record' });
      }
    });
  }

  goBack() {
    this._router.navigate(['/tutor-signup/' + this.constants.tutorLanguagesUrl]);
  }

  updateEducation() {
    // Since each record is saved individually in real-time CRUD, 
    // we just need to validate if at least one is required (if applicable) and navigate.
    if (this.educationForm.get('do_you_have_higher_education_degree').value && this.education.length === 0) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please add at least one education record' });
      return;
    }

    this._router.navigate(['/tutor-signup/' + this.constants.tutorExperienceUrl]);
  }

  prepareEducationFormObj(tempObj: any = {}) {
    let educationControls: any = [];
    if (tempObj?.educations && tempObj?.educations.length > 0) {
      tempObj?.educations.forEach((element: any) => {
        educationControls.push(this.createEducationListForm(element));
      });
    }

    this.educationForm = this._formBuilder.group({
      do_you_have_higher_education_degree: [tempObj?.do_you_have_higher_education_degree || false],
      education: this._formBuilder.array(educationControls),
    });

    this.sortEducations();
  }

  confirmSkip() {
    this.displaySkipDialog = false;
    this._tutorServices.skipStep('education').subscribe({
      next: (res: any) => {
        if (res.success) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message || 'Step skipped successfully' });
          this._router.navigate(['/tutor-signup/' + this.constants.tutorExperienceUrl]);
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: res.message || 'Failed to skip step' });
        }
      },
      error: (err: any) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err.error?.message || 'Something went wrong' });
      }
    });
  }

  onSkip() {
    this.displaySkipDialog = true;
  }
}